/**
 * Booking Funnel Tracker
 * Lightweight JS SDK for tracking user journey through the booking flow.
 *
 * Usage:
 * 1. Include this script on your booking pages
 * 2. Initialize with: BookingFunnelTracker.init({ endpoint: '/apps/combined-listings/api/track' })
 * 3. Track events: BookingFunnelTracker.pageLoad(), BookingFunnelTracker.dateSelected('2026-03-15'), etc.
 */
(function(window) {
  'use strict';

  var STORAGE_KEY = 'pf_booking_session';
  var TICKET_TYPE_KEY = 'pf_booking_ticket_type';
  var ENTRY_SOURCE_KEY = '_pf_entry_source';
  var SESSION_TTL = 30 * 60 * 1000; // 30 minutes
  var SELF_DOMAINS = ['pennywellfarm.co.uk', 'www.pennywellfarm.co.uk', 'account.pennywellfarm.co.uk', 'pennywelldevon.myshopify.com'];

  /**
   * Funnel stages - must match server-side values
   */
  var STAGES = {
    PAGE_LOAD: 'PAGE_LOAD',
    TICKET_TYPE_SELECTED: 'TICKET_TYPE_SELECTED',
    DATE_SELECTED: 'DATE_SELECTED',
    TICKET_SELECTED: 'TICKET_SELECTED',
    UPSELL_INTERACTION: 'UPSELL_INTERACTION',
    RESERVATION_START: 'RESERVATION_START',
    RESERVATION_OK: 'RESERVATION_OK',
    RESERVATION_FAILED: 'RESERVATION_FAILED',
    CART_ADD: 'CART_ADD',
    CHECKOUT_START: 'CHECKOUT_START',
    ORDER_COMPLETE: 'ORDER_COMPLETE'
  };

  /**
   * Generate a unique session ID
   */
  function generateSessionId() {
    var timestamp = Date.now().toString(36);
    var random = Math.random().toString(36).substring(2, 10);
    return 'bft_' + timestamp + '_' + random;
  }

  /**
   * Get or create session ID with TTL refresh
   */
  function getSessionId() {
    var stored = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY));
    } catch (e) {
      // Ignore parse errors
    }

    var now = Date.now();

    if (stored && stored.id && stored.expires > now) {
      // Refresh TTL on activity
      stored.expires = now + SESSION_TTL;
      try {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } catch (e) {
        // Ignore storage errors
      }
      return stored.id;
    }

    // Create new session - clear stored ticketType
    var newSession = {
      id: generateSessionId(),
      expires: now + SESSION_TTL
    };

    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newSession));
      sessionStorage.removeItem(TICKET_TYPE_KEY);
    } catch (e) {
      // Ignore storage errors
    }

    return newSession.id;
  }

  /**
   * Get stored ticket type for the current session
   */
  function getStoredTicketType() {
    try {
      return sessionStorage.getItem(TICKET_TYPE_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  /**
   * Store ticket type for the current session
   */
  function setStoredTicketType(ticketType) {
    try {
      sessionStorage.setItem(TICKET_TYPE_KEY, ticketType);
    } catch (e) {
      // Ignore storage errors
    }
  }

  /**
   * Detect device type from user agent
   */
  function getDeviceType() {
    var ua = navigator.userAgent || '';

    if (/tablet|ipad|playbook|silk/i.test(ua)) {
      return 'tablet';
    }

    if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) {
      return 'mobile';
    }

    return 'desktop';
  }

  /**
   * Get the original entry source (captured on first page load by entry-source-capture snippet).
   * Falls back to current page's referrer/UTMs if no entry source is stored.
   */
  function getEntrySource() {
    try {
      var stored = sessionStorage.getItem(ENTRY_SOURCE_KEY);
      if (stored) return JSON.parse(stored);
    } catch (e) {}
    return null;
  }

  /**
   * Check if a referrer URL is from our own domain (self-referral)
   */
  function isSelfReferral(referrer) {
    if (!referrer) return false;
    try {
      var host = new URL(referrer).hostname.toLowerCase();
      return SELF_DOMAINS.indexOf(host) !== -1;
    } catch (e) {
      return false;
    }
  }

  /**
   * Extract UTM parameters from URL
   */
  function getUtmParams() {
    var params = {};
    try {
      var search = window.location.search;
      var urlParams = new URLSearchParams(search);
      var utmKeys = ['utm_source', 'utm_medium', 'utm_campaign'];

      utmKeys.forEach(function(key) {
        var value = urlParams.get(key);
        if (value) {
          // Convert utm_source to utmSource
          var camelKey = key.replace(/_([a-z])/g, function(g) { return g[1].toUpperCase(); });
          params[camelKey] = value;
        }
      });
    } catch (e) {
      // Ignore errors
    }
    return params;
  }

  /**
   * Convert YYYY-MM-DD to DD-MM format
   */
  function toDayMonth(isoDate) {
    if (!isoDate || typeof isoDate !== 'string') return null;
    var parts = isoDate.split('-');
    if (parts.length !== 3) return null;
    return parts[2] + '-' + parts[1]; // DD-MM
  }

  /**
   * Main tracker object
   */
  var BookingFunnelTracker = {
    config: {
      endpoint: '/apps/combined-listings/api/track',
      debug: false
    },

    /**
     * Initialize the tracker
     * @param {Object} options - { endpoint: string, debug: boolean }
     */
    init: function(options) {
      if (options) {
        if (options.endpoint) this.config.endpoint = options.endpoint;
        if (options.debug) this.config.debug = true;
      }

      if (this.config.debug) {
        console.log('[BookingFunnelTracker] Initialized with config:', this.config);
      }

      return this;
    },

    /**
     * Send tracking event (fire-and-forget)
     * @param {string} stage - Funnel stage
     * @param {Object} data - Additional event data
     */
    track: function(stage, data) {
      var self = this;
      data = data || {};

      var currentReferrer = document.referrer || null;
      var entrySource = getEntrySource();

      // Use original entry source when current referrer is a self-referral
      var effectiveReferrer = currentReferrer;
      var effectiveUtm = getUtmParams();

      if (entrySource && isSelfReferral(currentReferrer)) {
        effectiveReferrer = entrySource.referrer || null;
        // Use entry UTMs if current page has none
        if (!effectiveUtm.utmSource && entrySource.utmSource) {
          effectiveUtm.utmSource = entrySource.utmSource;
        }
        if (!effectiveUtm.utmMedium && entrySource.utmMedium) {
          effectiveUtm.utmMedium = entrySource.utmMedium;
        }
        if (!effectiveUtm.utmCampaign && entrySource.utmCampaign) {
          effectiveUtm.utmCampaign = entrySource.utmCampaign;
        }
      }

      var payload = {
        sessionId: getSessionId(),
        stage: stage,
        deviceType: getDeviceType(),
        referrer: effectiveReferrer
      };

      // Auto-include stored ticketType if not already in data
      var storedType = getStoredTicketType();
      if (storedType && !data.ticketType) {
        payload.ticketType = storedType;
      }

      // Merge UTM params
      Object.keys(effectiveUtm).forEach(function(key) {
        payload[key] = effectiveUtm[key];
      });

      // Merge additional data
      Object.keys(data).forEach(function(key) {
        payload[key] = data[key];
      });

      if (this.config.debug) {
        console.log('[BookingFunnelTracker] Tracking:', stage, payload);
      }

      // Use sendBeacon for reliable fire-and-forget
      if (navigator.sendBeacon) {
        try {
          var blob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
          var success = navigator.sendBeacon(this.config.endpoint, blob);

          if (this.config.debug) {
            console.log('[BookingFunnelTracker] sendBeacon:', success ? 'success' : 'failed');
          }

          if (success) return;
        } catch (e) {
          // Fall through to fetch
        }
      }

      // Fallback to fetch
      fetch(this.config.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(function(error) {
        if (self.config.debug) {
          console.error('[BookingFunnelTracker] Fetch error:', error);
        }
      });
    },

    /**
     * Track page load
     */
    pageLoad: function() {
      this.track(STAGES.PAGE_LOAD);
      return this;
    },

    /**
     * Track date selection
     * @param {string} date - Selected date in YYYY-MM-DD format
     */
    dateSelected: function(date) {
      this.track(STAGES.DATE_SELECTED, {
        selectedDate: date,
        selectedDayMonth: toDayMonth(date)
      });
      return this;
    },

    /**
     * Track ticket selection
     * @param {number} quantity - Total ticket count
     * @param {Object} details - Breakdown by ticket type { adult: 2, child: 1 }
     */
    ticketSelected: function(quantity, details) {
      this.track(STAGES.TICKET_SELECTED, {
        ticketQuantity: quantity,
        ticketDetails: details || null
      });
      return this;
    },

    /**
     * Track reservation start
     * @param {string} date - Selected date in YYYY-MM-DD format
     * @param {number} quantity - Total ticket count
     */
    reservationStart: function(date, quantity) {
      this.track(STAGES.RESERVATION_START, {
        selectedDate: date,
        selectedDayMonth: toDayMonth(date),
        ticketQuantity: quantity
      });
      return this;
    },

    /**
     * Track successful reservation
     * @param {string} reservationId - The reservation ID returned from API
     */
    reservationOk: function(reservationId) {
      this.track(STAGES.RESERVATION_OK, {
        reservationId: reservationId
      });
      return this;
    },

    /**
     * Track failed reservation
     * @param {string} errorCode - Error code or message
     */
    reservationFailed: function(errorCode) {
      this.track(STAGES.RESERVATION_FAILED, {
        errorCode: errorCode
      });
      return this;
    },

    /**
     * Track ticket type selection (day tickets, season tickets, etc.)
     * @param {string} ticketType - The ticket type selected
     */
    ticketTypeSelected: function(ticketType) {
      setStoredTicketType(ticketType);
      this.track(STAGES.TICKET_TYPE_SELECTED, {
        ticketType: ticketType
      });
      return this;
    },

    /**
     * Track upsell interaction (view, add, skip)
     * @param {string} action - The upsell action (view, add, skip)
     * @param {Object} details - Additional details about the interaction
     */
    upsellInteraction: function(action, details) {
      this.track(STAGES.UPSELL_INTERACTION, {
        ticketDetails: Object.assign({ upsellAction: action }, details || {})
      });
      return this;
    },

    /**
     * Track cart add
     */
    cartAdd: function() {
      this.track(STAGES.CART_ADD);
      return this;
    },

    /**
     * Track checkout start
     */
    checkoutStart: function() {
      this.track(STAGES.CHECKOUT_START);
      return this;
    },

    /**
     * Track order completion
     * @param {number} orderId - Shopify order ID
     */
    orderComplete: function(orderId) {
      this.track(STAGES.ORDER_COMPLETE, {
        orderId: orderId
      });
      return this;
    },

    /**
     * Get current session ID (for debugging)
     */
    getSessionId: function() {
      return getSessionId();
    },

    /**
     * Expose stages for external use
     */
    STAGES: STAGES
  };

  // Expose to global scope
  window.BookingFunnelTracker = BookingFunnelTracker;

})(window);
